// src/components/timetable/modern/useModernTimetable.ts
//
// Ported from the mobile app's useTimetable.ts. Session id comes from
// useSchoolDetailsStore (matches TimeSlotsManager.tsx / AddTimetableEntryModal.tsx)
// instead of the mobile useUserStore. RN's Animated.Value fade/slide is
// replaced with a plain `fadeIn` boolean the components turn into a CSS
// transition class — the derived-data and lookup logic is otherwise
// unchanged from the mobile hook.

import { useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';
import {
  AffectedSlot,
  AddArrangementPayload,
  ApiResponse,
  ArrangementPeriod,
  AttendanceApiResponse,
  ClassSection,
  ClassTimetable,
  DayOfWeek,
  TeacherArrangement,
  TeacherArrangementsApiResponse,
  TimetableItem,
  TimetableTypeOption,
  ViewMode,
  isScheduledForDay,
  todayDate,
} from './modernTimetableTypes';
import { useModernTimetableTypes } from './useModernTimetableTypes';

export interface UseModernTimetableReturn {
  // Data
  timetableData:    ClassTimetable[];
  presentTeachers:  Set<string>;
  timetableTypeName:string;

  // Derived
  allItems:         TimetableItem[];
  periodMap:        Map<string, { startTime: string; endTime: string }>;
  uniquePeriods:    string[];
  classSectionCols: ClassSection[];
  allTeachers:      string[];
  presentCount:     number;
  busyCount:        number;
  vacantCount:      number;
  arrangementPeriods: ArrangementPeriod[];

  // Arrangements API
  arrangements:           TeacherArrangement[];
  arrangementsLoading:    boolean;
  arrangementsError:      string | null;
  addArrangement:         (payload: AddArrangementPayload) => Promise<{ success: boolean; message: string }>;
  deleteArrangement:      (id: string) => Promise<{ success: boolean; message: string }>;
  fetchArrangements:      () => Promise<void>;

  // Loading / error
  initialLoading:   boolean;
  gridLoading:      boolean;
  error:            string | null;
  refreshing:       boolean;

  // UI state
  typeOptions:      TimetableTypeOption[];
  typeOptionsLoading: boolean;
  selectedTypeValue:string;
  setSelectedTypeValue: (v: string) => void;
  selectedDay:      DayOfWeek;
  setSelectedDay:   (d: DayOfWeek) => void;
  viewMode:         ViewMode;
  setViewMode:      (m: ViewMode) => void;
  selectedTeacher:  string | null;
  setSelectedTeacher: (t: string | null) => void;
  pickerVisible:    boolean;
  setPickerVisible: (v: boolean) => void;

  // Fade-in flag (web replacement for the mobile Animated values)
  fadeIn: boolean;

  // Actions
  onRefresh:      () => void;
  fetchTimetable: (isInitial?: boolean) => Promise<void>;

  // Lookup helpers
  findClassItem:        (classId: string, section: string | null, period: string) => TimetableItem | undefined;
  findTeacherItem:      (teacherName: string, period: string) => TimetableItem | undefined;
  findTeacherPeriodItem:(teacherName: string, period: string) => TimetableItem | undefined;
  isBreakPeriod:        (period: string) => boolean;
}

export const useModernTimetable = (): UseModernTimetableReturn => {
  const currentSessionId = useSchoolDetailsStore((state) => state.schoolDetails?.current_session?.id);
  const sessionId = currentSessionId ?? '';

  // Timetable types are fetched per school/session as Mongo ObjectID-backed
  // options — same as the mobile app.
  const { typeOptions, loading: typeOptionsLoading, error: typeOptionsError } = useModernTimetableTypes();

  const [timetableData,      setTimetableData]      = useState<ClassTimetable[]>([]);
  const [initialLoading,     setInitialLoading]     = useState(true);
  const [gridLoading,        setGridLoading]        = useState(false);
  const [error,              setError]              = useState<string | null>(null);
  const [refreshing,         setRefreshing]         = useState(false);
  const [selectedTypeValue,  setSelectedTypeValue]  = useState('');
  const [selectedDay,        setSelectedDay]        = useState<DayOfWeek>('Monday');
  const [timetableTypeName,  setTimetableTypeName]  = useState('');
  const [viewMode,           setViewMode]           = useState<ViewMode>('classes');
  const [selectedTeacher,    setSelectedTeacher]    = useState<string | null>(null);
  const [pickerVisible,      setPickerVisible]      = useState(false);
  const [presentTeachers,    setPresentTeachers]    = useState<Set<string>>(new Set());
  const [fadeIn,             setFadeIn]             = useState(false);
  const [, setTick] = useState(0);

  // Arrangements state
  const [arrangements,        setArrangements]        = useState<TeacherArrangement[]>([]);
  const [arrangementsLoading, setArrangementsLoading] = useState(false);
  const [arrangementsError,   setArrangementsError]   = useState<string | null>(null);

  const isFirstRender = useRef(true);

  // Tick every minute to keep live-period highlight fresh
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Once timetable types load, auto-select the active one (falls back to
  // the first option).
  useEffect(() => {
    if (selectedTypeValue || typeOptions.length === 0) return;
    const active = typeOptions.find((t) => t.status === 'active');
    setSelectedTypeValue((active ?? typeOptions[0]).value);
  }, [typeOptions, selectedTypeValue]);

  // Surface a failure to load timetable types as the main screen error,
  // since the timetable itself can't be fetched without a type id.
  useEffect(() => {
    if (typeOptionsError) {
      setError(typeOptionsError);
      setInitialLoading(false);
    }
  }, [typeOptionsError]);

  // ── Fetch attendance ────────────────────────────────────────────────────────
  const fetchAttendance = async () => {
    try {
      const today = todayDate();
      let page = 1;
      const checkedInNames = new Set<string>();

      // NOTE: sending user_role: 'teacher,principal' does an exact-match on
      // the backend and silently returns 0 results — omit user_role from the
      // request (caller gets all roles back) and filter client-side instead.
      while (true) {
        const res = await apiClient.get<AttendanceApiResponse>('api/staff-attendance', {
          params: { scan_type: 'checkin', date: today, page, limit: 150 },
        });

        if (res.data.status !== 'success') break;

        const { records, pagination } = res.data.data;
        records.forEach((r) => {
          if (
            r.user_name &&
            (r.user_role === 'teacher' || r.user_role === 'principal')
          ) {
            checkedInNames.add(r.user_name.trim());
          }
        });

        if (!pagination.has_next) break;
        page++;
      }

      setPresentTeachers(checkedInNames);
    } catch (err) {
      // Non-critical — timetable still renders without attendance data
      console.warn('Attendance fetch failed', err);
    }
  };

  // ── Fetch arrangements ──────────────────────────────────────────────────────
  const fetchArrangements = async () => {
    setArrangementsLoading(true);
    setArrangementsError(null);
    try {
      const res = await apiClient.get<TeacherArrangementsApiResponse>('teacher-arrangements');
      if (res.data.status === 'success') {
        setArrangements(res.data.data ?? []);
      } else {
        setArrangementsError(res.data.message ?? 'Failed to load arrangements');
      }
    } catch (err: any) {
      setArrangementsError(err.response?.data?.message ?? 'Failed to load arrangements');
    } finally {
      setArrangementsLoading(false);
    }
  };

  // ── Add arrangement ─────────────────────────────────────────────────────────
  const addArrangement = async (payload: AddArrangementPayload): Promise<{ success: boolean; message: string }> => {
    try {
      const res = await apiClient.post('teacher-arrangements', payload);
      if (res.data.status === 'success') {
        await fetchArrangements(); // refresh list
        return { success: true, message: res.data.message ?? 'Arrangement added.' };
      }
      return { success: false, message: res.data.message ?? 'Failed to add arrangement.' };
    } catch (err: any) {
      return { success: false, message: err.response?.data?.message ?? 'Failed to add arrangement.' };
    }
  };

  // ── Delete arrangement ──────────────────────────────────────────────────────
  const deleteArrangement = async (id: string): Promise<{ success: boolean; message: string }> => {
    try {
      const res = await apiClient.delete(`teacher-arrangements/${id}`);
      if (res.data.status === 'success') {
        setArrangements((prev) => prev.filter((a) => a.id !== id));
        return { success: true, message: res.data.message ?? 'Arrangement deleted.' };
      }
      return { success: false, message: res.data.message ?? 'Failed to delete arrangement.' };
    } catch (err: any) {
      return { success: false, message: err.response?.data?.message ?? 'Failed to delete arrangement.' };
    }
  };

  // ── Fetch timetable ─────────────────────────────────────────────────────────
  const fetchTimetable = async (isInitial = false) => {
    if (!sessionId || !selectedTypeValue) {
      if (isInitial) setInitialLoading(false);
      return;
    }

    setError(null);
    isInitial ? setInitialLoading(true) : setGridLoading(true);
    try {
      const res = await apiClient.get<ApiResponse>('api/timetables-by-type', {
        params: {
          timetable_type_id: selectedTypeValue,
          session_id: sessionId,
        },
      });

      if (res.data.status === 'success') {
        setTimetableData(res.data.data.classes);
        setTimetableTypeName(res.data.data.timetable_type_name);
        setFadeIn(false);
        // Next tick so the CSS transition actually triggers
        requestAnimationFrame(() => setFadeIn(true));
      } else {
        setError(res.data.message || 'Failed to fetch timetable');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch timetable');
    } finally {
      setInitialLoading(false);
      setGridLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchTimetable(false);
    fetchAttendance();
    fetchArrangements();
  };

  // Attendance/arrangements don't depend on the type/session resolution, so
  // they still kick off immediately on mount.
  useEffect(() => {
    fetchAttendance();
    fetchArrangements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timetable fetch waits for both sessionId and selectedTypeValue to be
  // ready, then refetches whenever either changes.
  useEffect(() => {
    if (!sessionId || !selectedTypeValue) return;
    const initial = isFirstRender.current;
    isFirstRender.current = false;
    fetchTimetable(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, selectedTypeValue]);

  // ── Derived data ────────────────────────────────────────────────────────────

  const allItems = useMemo(() => timetableData.flatMap((c) => c.schedule), [timetableData]);

  const periodMap = useMemo(() => {
    const m = new Map<string, { startTime: string; endTime: string }>();
    allItems.forEach((i) => {
      if (!m.has(i.period_label))
        m.set(i.period_label, { startTime: i.start_time, endTime: i.end_time });
    });
    return m;
  }, [allItems]);

  const uniquePeriods = useMemo(() => Array.from(periodMap.keys()).sort(), [periodMap]);

  const classSectionCols = useMemo<ClassSection[]>(() => {
    const cols: ClassSection[] = [];
    timetableData.forEach((cls) => {
      const sections = Array.from(
        new Set(
          cls.schedule
            .map((i) => i.section || null)
            .filter((sec): sec is string => sec !== null)
        )
      ).sort();
      if (sections.length === 0)
        cols.push({ classId: cls.class_id, className: cls.class_name, section: null });
      else
        sections.forEach((sec) =>
          cols.push({ classId: cls.class_id, className: cls.class_name, section: sec })
        );
    });
    return cols;
  }, [timetableData]);

  const allTeachers = useMemo(() => {
    const set = new Set<string>();
    allItems.forEach((i) => {
      if (i.teacher_name && i.slot_type !== 'break') set.add(i.teacher_name);
    });
    return Array.from(set).sort();
  }, [allItems]);

  // Auto-select first teacher when switching to single_teacher view
  useEffect(() => {
    if (viewMode === 'single_teacher' && !selectedTeacher && allTeachers.length > 0)
      setSelectedTeacher(allTeachers[0]);
  }, [viewMode, allTeachers]);

  // ── Lookup helpers ──────────────────────────────────────────────────────────

  const isBreakPeriod = (period: string): boolean =>
    allItems.some((i) => i.period_label === period && i.slot_type === 'break');

  const findClassItem = (classId: string, section: string | null, period: string): TimetableItem | undefined => {
    const cls = timetableData.find((c) => c.class_id === classId);
    if (!cls) return undefined;
    const normalizedSection = section || null;
    const c = cls.schedule.filter(
      (item) => (item.section || null) === normalizedSection && item.period_label === period
    );
    if (c.length > 1) return c.find((i) => isScheduledForDay(i, selectedDay));
    return c.length === 1 && isScheduledForDay(c[0], selectedDay) ? c[0] : undefined;
  };

  const findTeacherItem = (teacherName: string, period: string): TimetableItem | undefined =>
    allItems.find(
      (i) => i.teacher_name === teacherName &&
             i.period_label === period &&
             i.slot_type !== 'break' &&
             isScheduledForDay(i, selectedDay)
    );

  const findTeacherPeriodItem = (teacherName: string, period: string): TimetableItem | undefined =>
    allItems.find(
      (i) => i.teacher_name === teacherName &&
             i.period_label === period &&
             isScheduledForDay(i, selectedDay)
    );

  // ── Counts ──────────────────────────────────────────────────────────────────

  const busyCount = useMemo(
    () => uniquePeriods.filter((p) => selectedTeacher && !isBreakPeriod(p) && !!findTeacherItem(selectedTeacher, p)).length,
    [selectedTeacher, uniquePeriods, selectedDay, allItems]
  );

  const vacantCount = useMemo(
    () => uniquePeriods.filter((p) => selectedTeacher && !isBreakPeriod(p) && !findTeacherItem(selectedTeacher, p)).length,
    [selectedTeacher, uniquePeriods, selectedDay, allItems]
  );

  const presentCount = useMemo(
    () => allTeachers.filter((t) => presentTeachers.has(t)).length,
    [allTeachers, presentTeachers]
  );

  // ── Arrangements derived data ────────────────────────────────────────────────
  // For each period on selectedDay:
  //   vacantTeachers  — teachers with NO class scheduled this period (free)
  //   absentTeachers  — teachers WITH a class this period but NO check-in today
  //   affectedSlots   — class · section · subject for each absent teacher's slot

  const arrangementPeriods = useMemo<ArrangementPeriod[]>(() => {
    return uniquePeriods.map((period) => {
      const times  = periodMap.get(period)!;
      const isBreak = allItems.some((i) => i.period_label === period && i.slot_type === 'break');
      const isLive  = (() => {
        const now = new Date();
        const nowMins = now.getHours() * 60 + now.getMinutes();
        const [sh, sm] = times.startTime.split(':').map(Number);
        const [eh, em] = times.endTime.split(':').map(Number);
        return nowMins >= sh * 60 + sm && nowMins < eh * 60 + em;
      })();

      if (isBreak) {
        return { period, startTime: times.startTime, endTime: times.endTime, isBreak: true, isLive, vacantTeachers: [], absentTeachers: [] };
      }

      // Items scheduled for this period on selectedDay (excluding breaks)
      const scheduledItems = allItems.filter(
        (i) => i.period_label === period &&
               i.slot_type !== 'break' &&
               isScheduledForDay(i, selectedDay)
      );

      // Set of teachers scheduled this period
      const scheduledTeacherNames = new Set(scheduledItems.map((i) => i.teacher_name).filter(Boolean));

      // Vacant = teacher in allTeachers with NO scheduled item this period
      const vacantTeachers = allTeachers.filter((t) => !scheduledTeacherNames.has(t));

      // Absent = scheduled this period but NOT in presentTeachers
      const absentTeacherNames = Array.from(scheduledTeacherNames).filter(
        (name) => name && !presentTeachers.has(name)
      );

      const absentTeachers = absentTeacherNames.map((name) => {
        // All slots this absent teacher has this period
        const slots = scheduledItems.filter((i) => i.teacher_name === name);
        const affectedSlots: AffectedSlot[] = slots.map((slot) => {
          const cls = timetableData.find((c) => c.schedule.some((s) => s.id === slot.id));
          return {
            teacherName: name,
            className:   cls?.class_name ?? '',
            section:     slot.section,
            subject:     slot.subject_name,
          };
        });
        return { name, affectedSlots };
      });

      return {
        period,
        startTime:  times.startTime,
        endTime:    times.endTime,
        isBreak:    false,
        isLive,
        vacantTeachers,
        absentTeachers,
      };
    });
  }, [uniquePeriods, periodMap, allItems, allTeachers, presentTeachers, selectedDay, timetableData]);

  return {
    timetableData,
    presentTeachers,
    timetableTypeName,
    allItems,
    periodMap,
    uniquePeriods,
    classSectionCols,
    allTeachers,
    presentCount,
    busyCount,
    vacantCount,
    arrangementPeriods,
    arrangements,
    arrangementsLoading,
    arrangementsError,
    addArrangement,
    deleteArrangement,
    fetchArrangements,
    initialLoading,
    gridLoading,
    error,
    refreshing,
    typeOptions,
    typeOptionsLoading,
    selectedTypeValue,
    setSelectedTypeValue,
    selectedDay,
    setSelectedDay,
    viewMode,
    setViewMode,
    selectedTeacher,
    setSelectedTeacher,
    pickerVisible,
    setPickerVisible,
    fadeIn,
    onRefresh,
    fetchTimetable,
    findClassItem,
    findTeacherItem,
    findTeacherPeriodItem,
    isBreakPeriod,
  };
};

export default useModernTimetable;
